import { createSlice } from "@reduxjs/toolkit";

const automationTemplateDefault = {
  mode: 1, // 1=View, 2=Add, 3=Edit
  formData: null,   // original record from API (used as reference for edit)
  formValues: null, // current user input (persisted across navigation)
  hasUnsavedChanges: false,
  searchValue: "",  // persisted search filter
};

// null = "not yet initialized" (first visit or right after save)
// ""   = user explicitly cleared the field
// The component uses ?? so only null falls through to the saved-profile fallback.
const userProfileDefault = {
  firstName: null,
  lastName: null,
};

export const settingsSlice = createSlice({
  name: "settings",
  initialState: {
    collapseId: "",
    pageId: "user-profile",
    automationTemplates: {
      smsTemplate: { ...automationTemplateDefault },
      emailTemplate: { ...automationTemplateDefault, templateType: null },
      emailSignature: { ...automationTemplateDefault },
      rolesPermissions: { ...automationTemplateDefault },
      apiKeys: { ...automationTemplateDefault },
      leadStatus: { ...automationTemplateDefault },
      leadSource: { ...automationTemplateDefault },
      leadTags: { ...automationTemplateDefault },
      leadScoring: { ...automationTemplateDefault },
      taskSettings: {
        taskStatus: { ...automationTemplateDefault },
        taskType: { ...automationTemplateDefault },
        taskPriority: { ...automationTemplateDefault },
        taskReminder: { ...automationTemplateDefault },
      },
    },
    // Persists unsaved edits on the User Profile page across sidebar navigation
    userProfile: { ...userProfileDefault },
    // Search field for the standalone Appointment Members settings page
    appointmentMembersSearch: "",
    // Persists Table vs Grid toggle for Media Storage across navigation
    mediaStorageViewMode: 1, // 1=Table, 2=Grid
    // Persists active tab for Advance Email Settings across navigation
    advanceEmailSettingsTab: 1, // 1=Domains, 2=From Email Address, 3=Reply-to Email
    // Persists domain verify/edit screen across navigation
    advanceEmailDomainVerify: {
      verifyMode: false,
      domainData: null,
    },
  },

  reducers: {
    setCollapseId: (state, action) => {
      state.collapseId = action.payload;
    },
    setPageId: (state, action) => {
      state.pageId = action.payload;
    },
    setAutomationTemplateState: (state, action) => {
      const { key, ...fields } = action.payload;
      const [parentKey, subKey] = key.split(".");
      if (subKey) {
        if (state.automationTemplates[parentKey]?.[subKey] !== undefined) {
          state.automationTemplates[parentKey][subKey] = {
            ...state.automationTemplates[parentKey][subKey],
            ...fields,
          };
        }
      } else if (state.automationTemplates[parentKey]) {
        state.automationTemplates[parentKey] = {
          ...state.automationTemplates[parentKey],
          ...fields,
        };
      }
    },
    clearAutomationTemplateState: (state, action) => {
      const key = action.payload;
      const [parentKey, subKey] = key.split(".");
      if (subKey) {
        const currentSearchValue =
          state.automationTemplates[parentKey]?.[subKey]?.searchValue ?? "";
        state.automationTemplates[parentKey][subKey] = {
          ...automationTemplateDefault,
          searchValue: currentSearchValue,
        };
      } else {
        const currentSearchValue =
          state.automationTemplates[parentKey]?.searchValue ?? "";
        if (parentKey === "emailTemplate") {
          state.automationTemplates[parentKey] = {
            ...automationTemplateDefault,
            templateType: null,
            searchValue: currentSearchValue,
          };
        } else {
          state.automationTemplates[parentKey] = {
            ...automationTemplateDefault,
            searchValue: currentSearchValue,
          };
        }
      }
    },
    setUserProfileFields: (state, action) => {
      const { firstName, lastName } = action.payload;
      if (firstName !== undefined) state.userProfile.firstName = firstName;
      if (lastName !== undefined) state.userProfile.lastName = lastName;
    },
    clearUserProfileFields: (state) => {
      state.userProfile = { ...userProfileDefault };
    },
    setAppointmentMembersSearch: (state, action) => {
      state.appointmentMembersSearch = action.payload;
    },
    clearAppointmentMembersSearch: (state) => {
      state.appointmentMembersSearch = "";
    },
    setMediaStorageViewMode: (state, action) => {
      state.mediaStorageViewMode = action.payload;
    },
    setAdvanceEmailSettingsTab: (state, action) => {
      state.advanceEmailSettingsTab = action.payload;
    },
    setAdvanceEmailDomainVerify: (state, action) => {
      state.advanceEmailDomainVerify = {
        ...state.advanceEmailDomainVerify,
        ...action.payload,
      };
    },
    clearAdvanceEmailDomainVerify: (state) => {
      state.advanceEmailDomainVerify = {
        verifyMode: false,
        domainData: null,
      };
    },
  },
});

export const {
  setCollapseId,
  setPageId,
  setAutomationTemplateState,
  clearAutomationTemplateState,
  setUserProfileFields,
  clearUserProfileFields,
  setAppointmentMembersSearch,
  clearAppointmentMembersSearch,
  setMediaStorageViewMode,
  setAdvanceEmailSettingsTab,
  setAdvanceEmailDomainVerify,
  clearAdvanceEmailDomainVerify,
} = settingsSlice.actions;

export const getCollapseId = (state) => state.settings.collapseId;
export const getPageId = (state) => state.settings.pageId;
export const getSmsTemplatePersistedState = (state) =>
  state.settings.automationTemplates.smsTemplate;
export const getEmailTemplatePersistedState = (state) =>
  state.settings.automationTemplates.emailTemplate;
export const getEmailSignaturePersistedState = (state) =>
  state.settings.automationTemplates.emailSignature;
export const getUserProfileFields = (state) => state.settings.userProfile;
export const getAppointmentMembersSearch = (state) =>
  state.settings.appointmentMembersSearch;
export const getRolesPermissionsPersistedState = (state) =>
  state.settings.automationTemplates.rolesPermissions;
export const getApiKeysPersistedState = (state) =>
  state.settings.automationTemplates.apiKeys;
export const getLeadStatusPersistedState = (state) =>
  state.settings.automationTemplates.leadStatus;
export const getLeadSourcePersistedState = (state) =>
  state.settings.automationTemplates.leadSource;
export const getLeadTagsPersistedState = (state) =>
  state.settings.automationTemplates.leadTags;
export const getLeadScoringPersistedState = (state) =>
  state.settings.automationTemplates.leadScoring;
export const getTaskStatusPersistedState = (state) =>
  state.settings.automationTemplates.taskSettings.taskStatus;
export const getTaskTypePersistedState = (state) =>
  state.settings.automationTemplates.taskSettings.taskType;
export const getTaskPriorityPersistedState = (state) =>
  state.settings.automationTemplates.taskSettings.taskPriority;
export const getTaskReminderPersistedState = (state) =>
  state.settings.automationTemplates.taskSettings.taskReminder;
export const getMediaStorageViewMode = (state) =>
  state.settings.mediaStorageViewMode;
export const getAdvanceEmailSettingsTab = (state) =>
  state.settings.advanceEmailSettingsTab;
export const getAdvanceEmailDomainVerify = (state) =>
  state.settings.advanceEmailDomainVerify;

export default settingsSlice.reducer;
